import { FC, useMemo } from 'react';
import { BenefitT } from '../../../../lib/types/sections';
import styles from './index.module.scss';
import { EndUserTypography } from '../../../../components/EndUserTypography';
import { IconComponent } from '../../../../components/IconComponent';
import { useNavigate } from 'react-router-dom';
import { SectionName } from '../../../../lib/models/Section';
import { setCurrentCategory } from '../../../../lib/store/slices/endUserSlice';
import { useAppDispatch, useAppSelector } from '../../../../lib/store/hooks';
import { END_USER_FRENCH_TENANT_MODEL_NAME } from '../../../../lib/utils/constants';
import { ROUTER_PATHS } from '../../../../lib/utils/router-paths';
import { openLinkAndTrack } from '../../../../lib/utils/sendDataToAnalytics';
import { useIsEnergieScwabenTenant } from '../../../../lib/hooks/endUserHooks/useIsEnergieScwabenTenant';
import { useHardcodedRedirectToCategory } from '../../../../lib/hooks/endUserHooks/useHardcodedRedirectToCategory';
import { FoundElementT } from '../../../../lib/hooks/endUserHooks/useGetCampaignObjectPositions';
import { CampaignObjectImage } from '../../../../components/CampaignObjectImage';
import { CAMPAIGN_POSITION_ELEMENT_OPTIONS } from '../../../campaignDetails/hooks/useGetPositionOption';
import cn from 'classnames';

type Props = BenefitT & {
  backgroundColor?: string;
  iconColor?: string;
  fontColor?: string;
  campaignObject?: FoundElementT | null;
};

export const BenefitCard: FC<Props> = ({
  headline4,
  description,
  icon,
  fontColor,
  iconColor,
  backgroundColor,
  link,
  campaignObject,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { demandArea } = useAppSelector(({ endUser }) => endUser);
  const isFrenchTenant = demandArea?.project?.name === END_USER_FRENCH_TENANT_MODEL_NAME;
  const { isEnergieSchwaben } = useIsEnergieScwabenTenant();
  const { hardcodedRedirect } = useHardcodedRedirectToCategory();

  const handleClickCard = () => {
    if (typeof link === 'string') {
      openLinkAndTrack(link);
    } else if (link) {
      if (isEnergieSchwaben && headline4 === 'Faire Preise') {
        hardcodedRedirect();

        const path = isFrenchTenant ? ROUTER_PATHS.houseModelFr : ROUTER_PATHS.houseModel;
        navigate(`/${path}/${link.demandAreaId}`, {
          state: { scrollTo: SectionName.PRODUCT_DETAILS },
        });
      } else {
        const path = isFrenchTenant ? ROUTER_PATHS.houseModelFr : ROUTER_PATHS.houseModel;

        navigate(`/${path}/${link.demandAreaId}`, {
          state: { scrollTo: SectionName.PRODUCT_DETAILS },
        });

        setTimeout(() => {
          dispatch(setCurrentCategory({ category: { id: link.categoryId } }));
        }, 10);
      }
    }
  };

  const CampaignBenefitIconElement = useMemo(() => {
    if (campaignObject) {
      const isIcon = campaignObject.type.split('-')[1] === 'icon';

      if (isIcon) {
        return (
          <CampaignObjectImage
            containerClassName={styles.campaignObjectContainer}
            campaignObject={campaignObject}
          />
        );
      }
    }

    return null;
  }, [campaignObject]);

  const isEdgeElement =
    campaignObject &&
    (campaignObject.type === CAMPAIGN_POSITION_ELEMENT_OPTIONS.CONTAINER_ONE ||
      campaignObject.type === CAMPAIGN_POSITION_ELEMENT_OPTIONS.CONTAINER_TWO ||
      campaignObject.type === CAMPAIGN_POSITION_ELEMENT_OPTIONS.CONTAINER_THREE);

  const CampaignBenefitEdgeElement = useMemo(() => {
    if (campaignObject) {
      if (isEdgeElement) {
        return (
          <div className={styles.campaignObjectEdgeContainer}>
            <CampaignObjectImage campaignObject={campaignObject} />
          </div>
        );
      }
    }
  }, [campaignObject, isEdgeElement]);

  return (
    <div
      className={styles.wrapper}
      style={{
        backgroundColor: backgroundColor,
        color: fontColor,
        cursor: link ? 'pointer' : 'default',
      }}
      onClick={handleClickCard}
      role="presentation"
    >
      {CampaignBenefitIconElement ? (
        <>{CampaignBenefitIconElement}</>
      ) : (
        <IconComponent className={styles.icon} iconData={icon} color={iconColor} />
      )}

      {CampaignBenefitEdgeElement}
      <EndUserTypography
        type="headlineFourth"
        component="Title"
        level={4}
        className={styles.title}
      >
        {headline4}
      </EndUserTypography>
      <EndUserTypography
        type="body"
        component="Text"
        className={cn(styles.description, isEdgeElement && styles.descriptionRightGap)}
      >
        {description}
      </EndUserTypography>
    </div>
  );
};
